import dayjs from 'dayjs';
import 'dayjs/locale/th';
import parse from 'html-react-parser';
import _ from 'lodash';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import { useRouter } from 'next/router';
import numeral from 'numeral';
import PageError from 'pages/404';
import { FaEye, FaRegCalendarAlt } from 'react-icons/fa';
import CardV from 'src/components/cards-video/card-v';
import Layout from 'src/components/layouts';
import WidgetShare from 'src/components/widgets/widget-share';
import { APP_IMG, APP_URL, IS_NO_ROBOT_INDEX, NEXT_PUBLIC_DEFAULT_COUNT_VIEW, SHOW_COMMEMORATE } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_LOGO, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import client from 'src/utils/client';
import { getFeatureImage } from 'src/utils/contents/get-image';
import { DATALAYER_TYPE, sendDataLayerGlobal } from 'src/utils/datalayer/send-datalayer';
import { useDesktop } from 'src/utils/device';
import { escapeChar } from 'src/utils/helper';
import { GET } from 'src/utils/services';
import useUser from 'src/utils/useUser';

const PageVideoDetail = ({ article, related, lastNews, meta, dataAdsHeader }) => {
  const _data = useUser();
  const sessionSevId = _data?.user ? decryptionClient(_data?.user) : null;
  if (_.isEmpty(article)) return <PageError dataAdsHeader={dataAdsHeader} sessionSevId={sessionSevId} />;
  const router = useRouter();
  const dateUpdate = dayjs(article?.published_at).locale('th').add(543, 'year').format('DD MMM YYYY');
  const articleLink = `/live/${article.section.en}/${article.id}`;
  const baseUrlCategory = `${APP_URL}${router.asPath}`;
  /* ----------------------------------- SEO ---------------------------------- */
  const articleTitleSeo = escapeChar(article?.title) || WEB_TITLE;
  const articleDescriptionSeo = escapeChar(article?.description) || WEB_DESC;

  return (
    <>
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={article?.title ? `${article?.title}` : WEB_TITLE}
        description={article?.description || WEB_DESC}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
        openGraph={{
          type: 'article',
          locale: 'th_TH',
          site_name: WEB_NAME,
          title: article?.title ? `${article?.title}` : WEB_TITLE,
          description: article?.description || WEB_DESC,
          url: baseUrlCategory,
          article: {
            publishedTime: article?.published_at,
            modifiedTime: article?.updated_at,
            expirationTime: null,
            section: article?.section?.th,
            authors: [`${WEB_URL.FACEBOOK}`],
            tags: article?.tags
          },
          images: [
            {
              url: article?.image,
              width: '700',
              height: '395',
              alt: article?.title ? `${article?.title}` : WEB_TITLE
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: meta?.keyword || WEB_KEYWORD
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlCategory} />
        <meta property='twitter:title' content={article?.title ? `${article?.title}` : WEB_TITLE} />
        <meta property='twitter:description' content={article?.description || WEB_DESC} />
        <meta property='twitter:image' content={article?.image_url || IMAGE_DEFAULT} />
        {/* ----------------------------- ScriptBrowser ------------------------------------ */}
        {!useDesktop() && !_.isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_mobile}`) : ''}
        {useDesktop() && !_.isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_desktop}`) : ''}
        {/* ----------------------------- ADS : PPN ------------------------------- */}
        <script async type='text/javascript' src='https://securepubads.g.doubleclick.net/tag/js/gpt.js' />
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: 'var googletag = googletag || {}; window.googletag.cmd = googletag.cmd || [];'
          }}
        />
        {/* ------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                    __th_page="${article?.section?.en}";
                  `
          }}
        />

        {/* ---------------------------- SNIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                    {
                      "@context": "https://schema.org",
                      "@type": "WebSite",
                      "name": "${WEB_NAME}",
                      "url": "${WEB_URL.WEB}",
                      "description": "${articleDescriptionSeo}",
                      "sameAs": [
                        "${WEB_URL.FACEBOOK}",
                        "${WEB_URL.YOUTUBE}",
                        "${WEB_URL.INSTAGRAM}"
                      ],
                      "potentialAction": {
                        "@type": "SearchAction",
                        "target": "${WEB_URL.WEB}search?q={search_term_string}",
                        "query-input": "required name=search_term_string"
                      }
                    }
                  `
          }}
        />

        {/* ---------------------------- SNIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "WebPage", 
                      "name": "${articleTitleSeo}", 
                      "description": "${articleDescriptionSeo}", 
                      "publisher": { 
                        "@type": "ProfilePage", 
                        "name": "${WEB_NAME}" 
                      } 
                    } 
                  `
          }}
        />

        {/* ------------------------ SNIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "BreadcrumbList", 
                      "itemListElement": [ 
                        { 
                          "@type": "ListItem", 
                          "position": 1, 
                          "name": "หน้าแรก", 
                          "item": "${APP_URL}" 
                        }, 
                        { 
                          "@type": "ListItem", 
                          "position": 2, 
                          "name": "${article?.section?.th}", 
                          "item": "${APP_URL}/${article?.section?.en}" 
                        }, 
                        { 
                          "@type": "ListItem", 
                          "position": 3, 
                          "name": "${articleTitleSeo}", 
                          "item": "${article?.canonical || `${APP_URL}${article?.link}`}"  
                        } 
                      ] 
                    } 
                  `
          }}
        />

        {/* -------------------------- SNIPPET : NewsArticle ------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "NewsArticle", 
                      "mainEntityOfPage": { 
                        "@type": "WebPage", 
                        "@id": "${article?.canonical || `${APP_URL}${article?.link}`}"  
                      }, 
                      "headline": "${articleTitleSeo}", 
                      "alternativeHeadline": "${articleTitleSeo}", 
                      "description": "${articleDescriptionSeo}", 
                      "image": "${APP_IMG}${article?.image}", 
                      "datePublished": "${article?.published_at}",
                      "dateModified": "${article?.published_at}",
                      "author": { 
                        "@type": "Thing", 
                        "name": "${article.byline || WEB_NAME}",
                        "url": "${APP_URL}"
                      }, 
                      "publisher": { 
                        "@type": "Organization", 
                        "name": "${WEB_NAME}", 
                        "logo": { 
                          "@type": "ImageObject", 
                          "url": "${APP_IMG}/static/${WEB_LOGO}" 
                        } 
                      } 
                    } 
                  `
          }}
        />
      </Head>
      <Layout sessionSevId={sessionSevId}>
        <div className='container-video mb-5' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
          {/* ----------------------------- Breadcrumb -------------------------- */}
          <div className='row'>
            <div className='col-12'>
              <div className='category-wrapper'>
                <ul className='breadcrumb'>
                  <li>
                    <a href='/'>หน้าแรก</a>
                  </li>
                  <li>
                    <a href={'/live/video'}>วีดีโอ</a>
                  </li>
                </ul>
                {/* {article?.section?.th && <h1 className='heading-title'>{'วิดีโอ'}</h1>} */}
              </div>
            </div>
          </div>

          <div className='row'>
            <div className='col-12'>
              {/* ----------------------------- Article Iframe -------------------------- */}
              <div className='article-video-player'>{getFeatureImage(article)}</div>

              {/* ------------------------------ Article Info -------------------------- */}
              <div className='detail-wrapper '>
                <div className='main-content '>
                  <div className='content-heading-wrapper content-wrapper'>
                    <h1 className='content-title'>{article.title}</h1>
                    <div className='content-meta'>
                      <span>
                        <FaRegCalendarAlt />
                        {dateUpdate}
                      </span>
                      {article?.pageviews > 0 && article?.pageviews > Number(NEXT_PUBLIC_DEFAULT_COUNT_VIEW) && (
                        <span>
                          <FaEye />
                          {article?.pageviews > 999 ? numeral(article?.pageviews).format('0.0a') : article?.pageviews}
                        </span>
                      )}
                      <div className='justify-content-between align-items-center d-inline-flex'>
                        <span className='me-1 d-none d-lg-flex'>แชร์ </span>
                        <WidgetShare
                          link={articleLink}
                          routerPath={router.asPath}
                          //! DATALAYER
                          eventDataLayer={{
                            type: DATALAYER_TYPE.TRACK_POSITION,
                            router: router?.pathname,
                            section: 'แชร์ข่าว',
                            position: 'ใต้:ภาพปกข่าว',
                            data: {
                              heading: 'แชร์ข่าว'
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div
                      className='mt-3 content-detail'
                      dangerouslySetInnerHTML={{
                        __html: article.detail_1
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            {article.tags.length > 0 && (
              <div className='tags-wrapper'>
                <div className='tags-item'>
                  <span>แท็กที่เกี่ยวข้อง</span>
                  {article.tags.map((item, index) => (
                    <a
                      key={index}
                      href={`/tags/${item}`}
                      //! DATALAYER
                      onClick={() =>
                        sendDataLayerGlobal({
                          type: DATALAYER_TYPE.TRACK_POSITION,
                          router: router?.pathname,
                          section: 'แท็กที่เกี่ยวข้อง',
                          position: 'ใต้:เนื้อหาที่',
                          data: {
                            block: '1',
                            title: item,
                            heading: 'แท็กที่เกี่ยวข้อง',
                            index: index + 1
                          }
                        })
                      }
                    >
                      {item}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ------------------------------ Bottom Article -------------------------- */}
          <div className='col-12'>
            <div className='section-wrapper'>
              <div className='block-wrapper'>
                <div className='block-title --mt-0'>
                  {' '}
                  <h2>วีดีโอล่าสุด</h2>{' '}
                </div>
              </div>
              <div className='row'>
                {lastNews.slice(0, 4).map((item, index) => (
                  <div key={index} className='col-6 col-lg-3'>
                    <CardV
                      data={item}
                      path={`/live/${item.section.en}/${item.id}`}
                      //! DATALAYER
                      eventDataLayer={{
                        type: DATALAYER_TYPE.TRACK_POSITION,
                        router: router?.pathname,
                        section: 'วีดีโอล่าสุด',
                        position: 'ใต้:เนื้อหาที่-1',
                        data: {
                          title: item?.title,
                          heading: 'วีดีโอล่าสุด',
                          index: index + 1
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export async function getServerSideProps({ req, res, params, query }) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    article: null,
    related: null,
    lastNews: null,
    meta: null,
    dataAdsHeader: null
  };
  const scriptBrowser = await GET('/api/headerAds');
  if (!_.isEmpty(scriptBrowser)) {
    initProps.dataAdsHeader = scriptBrowser || '';
  }
  try {
    const { data } = await client.fetch.get(`/api/v1.0/video/contents/${params.id}`);
    // console.log('vdo-data', data);
    if ('msg' in data) {
      initProps.article = null;
    } else {
      initProps.article = data.data;
      initProps.related = data.related;
      initProps.lastNews = data.last_news;
      initProps.meta = data.meta;
    }
  } catch (err) {
    console.log('==========> ARTICLE DETAIL ERROR', err.message);
    initProps.errMessage = err.message;
    initProps.statusCode = err?.response?.status || 500;
  }
  // console.log('vdo-content-data', initProps.article);
  return {
    props: initProps
  };
}

export default PageVideoDetail;
